import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { audioAlerts } from '../utils/audioAlerts';

export type UnreadNotifItem = {
  id?: string;
  lidaEm?: string | null;
  titulo?: string | null;
  mensagem?: string | null;
  tipoNotificacao?: string | null;
  dataHora?: string | null;
  criadoEm?: string | null;
  aluno?: { id?: string; nome?: string; matricula?: string; fotoUrl?: string | null } | null;
  remetente?: { id?: string; nome?: string; perfil?: string; cargo?: string; fotoUrl?: string | null } | null;
  fotoUrl?: string | null;
  remetenteNome?: string | null;
  remetenteRole?: string | null;
};

export type LiveBanner = {
  title: string;
  body: string;
  notificacaoId?: string;
  fotoUrl?: string | null;
  nome?: string | null;
  role?: string | null;
};

type Options = {
  enabled: boolean;
  fetchItems: () => Promise<UnreadNotifItem[]>;
  /** Fallback se o item novo não tiver id. */
  actionHref: string;
  /** Href do toast "Ver" para o item mais novo (detalhe, não só a lista). */
  getActionHref?: (newest?: UnreadNotifItem | null) => string;
  onAnnounceItem?: (item: UnreadNotifItem | null) => void;
  pollMs?: number;
};

/**
 * Detecta aumento de não-lidas (poll) e avisa in-app quando o FCM falha.
 * Toast "Ver" aponta para o detalhe (`notificacaoId`), não só a listagem.
 */
export function useInAppUnreadAnnounce({
  enabled,
  fetchItems,
  actionHref,
  getActionHref,
  onAnnounceItem,
  pollMs = 8000,
}: Options) {
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [liveBanner, setLiveBanner] = useState<LiveBanner | null>(null);
  const [bellPulse, setBellPulse] = useState(false);
  const prevRef = useRef<number | null>(null);
  const fetchRef = useRef(fetchItems);
  fetchRef.current = fetchItems;
  const getHrefRef = useRef(getActionHref);
  getHrefRef.current = getActionHref;
  const onAnnounceRef = useRef(onAnnounceItem);
  onAnnounceRef.current = onAnnounceItem;

  const showBanner = useCallback((title: string, body: string, notificacaoId?: string, extra?: { fotoUrl?: string | null; nome?: string | null; role?: string | null }) => {
    setLiveBanner({
      title,
      body,
      notificacaoId,
      fotoUrl: extra?.fotoUrl || null,
      nome: extra?.nome || null,
      role: extra?.role || null,
    });
    setBellPulse(true);
    window.setTimeout(() => setBellPulse(false), 2500);
    window.setTimeout(() => setLiveBanner(null), 10000);
  }, []);

  const announce = useCallback(
    (title: string, body: string, newest?: UnreadNotifItem | null) => {
      try {
        audioAlerts.playRecebendoNotificacao();
      } catch { /* noop */ }
      onAnnounceRef.current?.(newest || null);
      showBanner(
        title || 'Nova mensagem',
        body || 'Toque para abrir.',
        newest?.id,
        {
          fotoUrl: newest?.fotoUrl || newest?.remetente?.fotoUrl || newest?.aluno?.fotoUrl || null,
          nome: newest?.remetenteNome || newest?.remetente?.nome || newest?.aluno?.nome || null,
          role: newest?.remetenteRole || newest?.remetente?.perfil || newest?.remetente?.cargo || null,
        }
      );
    },
    [showBanner],
  );

  const dismissBanner = useCallback(() => setLiveBanner(null), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    const run = async (skipAnnounce: boolean) => {
      try {
        const items = await fetchRef.current();
        if (cancelled) return;
        const list = Array.isArray(items) ? items : [];
        const pendentes = list.filter((n) => !n.lidaEm).length;
        const newest = list.find((n) => !n.lidaEm) || null;
        const prev = prevRef.current;

        if (!skipAnnounce && prev !== null && pendentes > prev) {
          announce(
            newest?.titulo || 'Nova notificação',
            newest?.mensagem || `${pendentes - prev} aviso(s) novo(s)`,
            newest,
          );
        } else if (skipAnnounce && prev !== null && pendentes > prev) {
          onAnnounceRef.current?.(newest);
          showBanner(
            newest?.titulo || 'Nova mensagem',
            newest?.mensagem || 'Toque para abrir.',
            newest?.id,
            {
              fotoUrl: newest?.fotoUrl || newest?.remetente?.fotoUrl || newest?.aluno?.fotoUrl || null,
              nome: newest?.remetenteNome || newest?.remetente?.nome || newest?.aluno?.nome || null,
              role: newest?.remetenteRole || newest?.remetente?.perfil || newest?.remetente?.cargo || null,
            }
          );
        }

        prevRef.current = pendentes;
        setUnreadCount(pendentes);
      } catch {
        /* 403/rede — silencioso */
      }
    };

    prevRef.current = null;
    run(true);

    const onFocus = () => run(true);
    const onPush = () => run(true);
    const onVis = () => {
      if (document.visibilityState === 'visible') run(true);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('ac-push-received', onPush);
    document.addEventListener('visibilitychange', onVis);
    const poll = window.setInterval(() => run(false), pollMs);

    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('ac-push-received', onPush);
      document.removeEventListener('visibilitychange', onVis);
      window.clearInterval(poll);
    };
  }, [enabled, pollMs, announce, showBanner]);

  return { unreadCount, liveBanner, bellPulse, dismissBanner, actionHref };
}
